import { useState } from "react";
import { FiCheck, FiChevronDown, FiChevronRight } from "@/lib/icons";
import type { Lender } from "../_data/lenders.data";

interface LenderCardProps {
  lender: Lender;
}

export function LenderCard({ lender }: LenderCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-start gap-4">
          {/* Logo */}
          <div className="w-16 h-16 bg-white border border-border rounded-lg flex items-center justify-center p-2 shrink-0">
            <img
              src={lender.logo}
              alt={lender.name}
              className="w-full h-full object-contain"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-xl mb-1">{lender.shortName}</h3>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {lender.description}
            </p>
          </div>
        </div>

        {/* Highlights */}
        {lender.highlights && (
          <div className="flex gap-4 mt-4">
            {lender.highlights.map((highlight) => (
              <div
                key={highlight.label}
                className="bg-cream rounded-xl px-4 py-2 text-center flex-1"
              >
                <p className="text-lg font-semibold text-primary">
                  {highlight.value}
                </p>
                <p className="text-xs text-muted-foreground">
                  {highlight.label}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Loan Types */}
        <div className="flex flex-wrap gap-2 mt-4">
          {lender.loanTypes.map((type) => (
            <span
              key={type}
              className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-lg"
            >
              {type}
            </span>
          ))}
        </div>
      </div>

      {/* Expand/Collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-6 py-3 bg-muted/50 hover:bg-muted transition-colors text-sm font-medium"
        aria-expanded={isExpanded}
      >
        <span>{isExpanded ? "Hide features" : "View features"}</span>
        {isExpanded ? (
          <FiChevronDown className="size-4" />
        ) : (
          <FiChevronRight className="size-4" />
        )}
      </button>

      {/* Features */}
      {isExpanded && (
        <div className="px-6 py-4 bg-muted/30 border-t border-border">
          <ul className="space-y-2">
            {lender.features.map((feature) => (
              <li key={feature} className="flex items-start gap-2 text-sm">
                <FiCheck className="size-4 text-primary shrink-0 mt-0.5" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
